'use client';

import { useState, useEffect } from 'react';
import type { Employee } from '@/app/hr/components/types';

interface MeHeaderProps {
  employee: Employee;
  supervisor: Employee | null;
  resolveAvatarUrl: (storagePath: string | null) => Promise<string | null>;
}

const STATUS_DOT_COLORS: Record<string, string> = {
  online: '#16a34a',
  away: '#F5A623',
  offline: '#9ca3af',
};

const AVATAR_BG_COLORS = [
  '#1C2E5E', '#E89A3C', '#7c3aed', '#16a34a', '#dc2626', '#F5A623',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_BG_COLORS[Math.abs(hash) % AVATAR_BG_COLORS.length];
}

const MeHeader = ({ employee, supervisor, resolveAvatarUrl }: MeHeaderProps) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!employee.avatar_url) { setAvatarUrl(null); return; }
    if (employee.avatar_url.startsWith('http')) {
      setAvatarUrl(employee.avatar_url);
      return;
    }
    let cancelled = false;
    resolveAvatarUrl(employee.avatar_url).then(url => {
      if (!cancelled && url) setAvatarUrl(url);
    });
    return () => { cancelled = true; };
  }, [employee.avatar_url, resolveAvatarUrl]);

  const initials = `${employee.first_name?.[0] ?? ''}${employee.last_name?.[0] ?? ''}`.toUpperCase();
  const avatarBg = getAvatarColor(`${employee.first_name}${employee.last_name}`);
  const dotColor = STATUS_DOT_COLORS[employee.status] ?? STATUS_DOT_COLORS.offline;

  const tenureMs = employee.hire_date
    ? Date.now() - new Date(employee.hire_date).getTime()
    : null;
  const tenureYears = tenureMs !== null ? Math.max(0, Math.floor(tenureMs / (365.25 * 24 * 60 * 60 * 1000))) : null;
  const tenureMonths = tenureMs !== null ? Math.max(0, Math.floor(tenureMs / (30.44 * 24 * 60 * 60 * 1000))) : null;
  const tenureLabel = tenureYears !== null && tenureYears >= 1
    ? `${tenureYears} year${tenureYears > 1 ? 's' : ''}`
    : tenureMonths !== null
      ? `${tenureMonths} month${tenureMonths !== 1 ? 's' : ''}`
      : '—';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-5">
      <div className="flex items-start gap-5">
        <div className="relative flex-shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-20 h-20 rounded-full object-cover" />
          ) : (
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl font-bold"
              style={{ background: avatarBg }}
            >
              {initials}
            </div>
          )}
          <span
            className="absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-white"
            style={{ background: dotColor }}
            title={employee.status}
          />
        </div>

        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-[#1C2E5E] truncate">
            {employee.preferred_name ?? employee.first_name} {employee.last_name}
          </h2>
          {employee.preferred_pronouns && (
            <p className="text-[12px] text-gray-500 mt-0.5">({employee.preferred_pronouns})</p>
          )}
          <p className="text-[15px] text-gray-700 mt-1">{employee.job_title}</p>
          <p className="text-[12px] text-gray-500">{employee.department}</p>

          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {employee.work_email && (
              <a href={`mailto:${employee.work_email}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                ✉ {employee.work_email}
              </a>
            )}
            {employee.work_phone && (
              <a href={`tel:${employee.work_phone}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
                📞 {employee.work_phone}
              </a>
            )}
            {employee.hire_date && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700">
                🗓 Hired {new Date(employee.hire_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} · {tenureLabel}
              </span>
            )}
            {supervisor ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700">
                👤 Reports to {supervisor.first_name} {supervisor.last_name}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">
                ⭐ Executive · No supervisor
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeHeader;
